import { useMemo, useCallback } from 'react';
import type { EventHandlers } from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import { useOnIssueKeyChange } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { CHANGE_EVENT_TYPES } from '@atlassian/jira-issue-view-child-issues-panel/src/common/constants.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';

import { fg } from '@atlassian/jira-feature-gating';
import { getOnChangeCallback } from '../../../../state/context/selectors.tsx';

const useEventHandlersOriginal = (currentIssueKey: string): EventHandlers => {
	const onIssueKeyChanged = useOnIssueKeyChange();
	const onIssueLinkSelected = useCallback(
		(issueKey: string): boolean => {
			onIssueKeyChanged &&
				onIssueKeyChanged({ fromIssueKey: currentIssueKey, toIssueKey: issueKey });
			return !!onIssueKeyChanged;
		},
		[currentIssueKey, onIssueKeyChanged],
	);

	return useMemo(
		() => ({
			onIssueLinkSelected,
		}),
		[onIssueLinkSelected],
	);
};

const useEventHandlersWithOnUpdateCache = (currentIssueKey: string): EventHandlers => {
	const onIssueKeyChanged = useOnIssueKeyChange();
	const onChange = useSelector(getOnChangeCallback);

	const onIssueLinkSelected = useCallback(
		(issueKey: string): boolean => {
			onIssueKeyChanged &&
				onIssueKeyChanged({ fromIssueKey: currentIssueKey, toIssueKey: issueKey });
			return !!onIssueKeyChanged;
		},
		[currentIssueKey, onIssueKeyChanged],
	);

	const onUpdateCache = useCallback(
		(issueKey: string, fieldName: string) => {
			if (fieldName === 'status' && onChange) {
				onChange({
					type: CHANGE_EVENT_TYPES.CHILD_ISSUE_STATUS_UPDATED,
					meta: {},
				});
			}
		},
		[onChange],
	);

	return useMemo(
		() => ({
			onIssueLinkSelected,
			onUpdateCache,
		}),
		[onIssueLinkSelected, onUpdateCache],
	);
};

export const useEventHandlers = functionWithCondition(
	() => fg('relay_migration_children_issues_section'),
	useEventHandlersWithOnUpdateCache,
	useEventHandlersOriginal,
);
